/**
 * ApiError is every failure the platform reports, thrown rather than
 * returned.
 *
 * The platform answers every failing route with the same RFC 9457 document
 * (internal/apperr), so there is exactly one error shape for the whole
 * surface and exactly one place a caller has to learn. `code` is the stable
 * part — a UI branches on `insufficient_funds`, never on 402, because the
 * status is a transport detail and the code is the contract.
 */
export class ApiError extends Error {
    status;
    code;
    problem;
    requestId;
    /** Per-field validation failures, ready to hand to a form resolver. */
    fieldErrors;
    constructor(status, problem, fallback) {
        super(problem?.detail || problem?.title || fallback);
        this.name = "ApiError";
        this.status = status;
        // A response that is not a problem document at all (a proxy's 502 page,
        // a connection reset rendered by the browser) still has to classify as
        // SOMETHING, and `internal` is the honest answer: the platform did not
        // tell us why.
        this.code = problem?.code ?? "internal";
        this.problem = problem;
        this.requestId = problem?.request_id;
        this.fieldErrors = problem?.errors ?? [];
    }
}
export function isApiError(err) {
    return err instanceof ApiError;
}
/** True when the caller is not signed in — the portals' redirect condition. */
export function isUnauthenticated(err) {
    return isApiError(err) && err.code === "unauthenticated";
}
/**
 * True when a wallet could not cover the request. Its own predicate because
 * it is the one failure with a specific, useful next step: top up.
 */
export function isInsufficientFunds(err) {
    return isApiError(err) && err.code === "insufficient_funds";
}
/**
 * NetworkError is a request that never reached the platform. It is NOT an
 * ApiError: nothing was refused, nothing was decided, and a UI that shows a
 * dropped Wi-Fi connection as "permission denied" sends the user hunting for
 * a permission problem that does not exist.
 */
export class NetworkError extends Error {
    cause;
    constructor(message, cause) {
        super(message);
        this.name = "NetworkError";
        this.cause = cause;
    }
}
//# sourceMappingURL=errors.js.map